import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ModuleBase, Box, ResponsiveImage, MediaLightboxCarousel } from '@/components';
import { listingGridItem } from '@/animations';
import { AnimatePresence, motion, useMotionValue } from 'framer-motion';
import ExpandIcon from '@/assets/expand.svg';

const carouselMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.2 } },
  exit: { opacity: 0 },
};

const TickerModule = ({ data }) => {
  const { images } = data || {};
  const [activeImage, setActiveImage] = useState(null);
  const [hasMounted, setHasMounted] = useState(false);
  const trackRef = useRef(null);
  const x = useMotionValue(0);
  const offsetRef = useRef(0);
  const rafRef = useRef(null);
  const isDragging = useRef(false);
  const isHovered = useRef(false);
  const lastPointerX = useRef(0);
  const velocityRef = useRef(0);
  const lastMoveTime = useRef(0);
  const momentumRef = useRef(null);
  const speed = 2; // pixels per frame
  const friction = 0.95; // momentum decay per frame
  const minVelocity = 0.2; // stop momentum below this

  const getHalfWidth = useCallback(() => {
    if (!trackRef.current) return 0;
    return trackRef.current.scrollWidth / 2;
  }, []);

  const wrapOffset = useCallback((half) => {
    if (half > 0) {
      if (offsetRef.current < -half) offsetRef.current += half;
      if (offsetRef.current > 0) offsetRef.current -= half;
    }
  }, []);

  const stopMomentum = useCallback(() => {
    if (momentumRef.current) {
      cancelAnimationFrame(momentumRef.current);
      momentumRef.current = null;
    }
  }, []);

  const startMomentum = useCallback(() => {
    stopMomentum();
    const decay = () => {
      velocityRef.current *= friction;
      if (Math.abs(velocityRef.current) < minVelocity) {
        velocityRef.current = 0;
        momentumRef.current = null;
        return;
      }
      offsetRef.current += velocityRef.current;
      wrapOffset(getHalfWidth());
      x.set(offsetRef.current);
      momentumRef.current = requestAnimationFrame(decay);
    };
    momentumRef.current = requestAnimationFrame(decay);
  }, [stopMomentum, getHalfWidth, x, wrapOffset]);

  const handlePointerDown = (e) => {
    // Ignore pointer down when interacting with controls (buttons/links/etc.)
    try {
      const targ = e.target || e.srcElement;
      if (targ && targ.closest && targ.closest('button, a, input, textarea, select, [role="button"]')) {
        return;
      }
    } catch (err) {
      // ignore
    }

    isDragging.current = true;
    stopMomentum();
    velocityRef.current = 0;
    lastPointerX.current = e.clientX;
    lastMoveTime.current = performance.now();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {
      // some browsers may throw if pointerId invalid
    }
  };

  const handlePointerMove = (e) => {
    if (!isDragging.current) return;
    const now = performance.now();
    const dx = e.clientX - lastPointerX.current;
    const dt = now - lastMoveTime.current || 1;
    velocityRef.current = (dx / dt) * 16; // normalize to ~per-frame velocity
    lastPointerX.current = e.clientX;
    lastMoveTime.current = now;
    offsetRef.current += dx;
    const half = getHalfWidth();
    wrapOffset(half);
    x.set(offsetRef.current);
  };

  const handlePointerUp = () => {
    isDragging.current = false;
    if (Math.abs(velocityRef.current) > minVelocity) {
      startMomentum();
    }
  };

  // Duplicate images once for seamless infinite loop
  const carouselImages = [];
  for (let i = 0; i < 2; i++) {
    carouselImages.push(
      ...images.map((image, index) => ({
        image,
        index,
      })),
    );
  }

  // Continuous auto-scroll loop (delta-time based for smooth animation during scroll)
  useEffect(() => {
    let lastTime = 0;
    const animate = (time) => {
      if (lastTime) {
        const delta = time - lastTime;
        if (!isDragging.current && !momentumRef.current && !isHovered.current) {
          offsetRef.current -= speed * (delta / 16); // normalize to 60fps baseline
          const half = getHalfWidth();
          if (half > 0 && Math.abs(offsetRef.current) >= half) {
            offsetRef.current += half;
          }
          x.set(offsetRef.current);
        }
      }
      lastTime = time;
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [x, getHalfWidth]);

  // Manual mount-triggered animation workaround
  useEffect(() => {
    const timeout = setTimeout(() => {
      setHasMounted(true);
    }, 50); // slight delay to ensure layout has finished
    return () => clearTimeout(timeout);
  }, []);

  return (
    <ModuleBase data={data} className="mx-auto w-full overflow-hidden">
      <MediaLightboxCarousel
        open={activeImage !== null}
        activeImage={activeImage}
        onClose={() => setActiveImage(null)}
        slides={images}
        preloadSlides={images}
        className="[&_.image-wrapper]:!h-auto"
      />

      <motion.div variants={carouselMotion} initial="initial" animate={hasMounted ? 'animate' : 'initial'} exit="exit">
        <AnimatePresence>
          <motion.div
            ref={trackRef}
            className="flex w-max cursor-grab select-none active:cursor-grabbing"
            style={{ x }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onMouseEnter={() => {
              isHovered.current = true;
            }}
            onMouseLeave={() => {
              isHovered.current = false;
            }}
          >
            {carouselImages.map((image, index) => (
              <Box
                key={`${index}-${image.index}`}
                motion={listingGridItem}
                className="group relative mx-1 h-[216px] flex-shrink-0 sm:h-[332px] lg:h-[432px]"
              >
                <ResponsiveImage
                  heights={{ xs: 216, sm: 332, lg: 432 }}
                  image={image.image}
                  imgProps={{ className: '!w-auto !h-full !relative pointer-events-none' }}
                />
                <div className="absolute right-6 top-4 transition-opacity duration-200 group-hover:opacity-100 lg:opacity-0">
                  <button
                    className="btn secondary bg-white/10 backdrop-blur-[6px]"
                    aria-label="Expand Image"
                    onClick={() => setActiveImage(image.index)}
                  >
                    <ExpandIcon />
                  </button>
                </div>
              </Box>
            ))}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </ModuleBase>
  );
};

export default TickerModule;
